/**
 * Leesbare weergave van generate_document (CalqCore OutputDocument)
 */
import { Printer } from 'lucide-react';
import { quoteBreakdownKeyNl } from '../lib/catalog-labels';
import { formatEuroNl, formatMeasureUnitNl, formatQuantityNl, humanizePricingLineDescription } from '../lib/format-nl';
import type { QuoteContent, QuoteDocument, QuoteSection } from '../types/project';

function ContentBlock({ c }: { c: QuoteContent }) {
  if (c.type === 'summary') {
    return (
      <div className="rounded-lg bg-surface p-4">
        <p className="mb-2 text-lg font-semibold text-text">
          {formatEuroNl(c.total)} <span className="text-sm font-normal text-text-muted">excl. BTW</span>
        </p>
        <ul className="space-y-1 text-sm text-text-muted">
          {Object.entries(c.breakdown).map(([k, v]) => (
            <li key={k} className="flex justify-between gap-4">
              <span>{quoteBreakdownKeyNl(k)}</span>
              <span className="tabular-nums text-text">{formatEuroNl(Number(v))}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (c.type === 'building_part_summary') {
    return (
      <ul className="space-y-3">
        {c.parts.map((p, i) => (
          <li
            key={i}
            className="rounded-lg border border-border/70 bg-surface/80 px-4 py-3 text-sm text-text"
          >
            <p className="font-semibold text-text">{p.part_label}</p>
            <p className="mt-1 text-xs text-text-muted">{p.onderbouwing}</p>
            <p className="mt-2 text-base font-semibold tabular-nums text-primary">
              {formatEuroNl(p.total_with_corrections)}{' '}
              <span className="text-xs font-normal text-text-muted">excl. BTW</span>
            </p>
          </li>
        ))}
      </ul>
    );
  }
  if (c.type === 'line_items') {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border text-text-muted">
              <th className="py-2 pr-2 font-medium">Omschrijving</th>
              <th className="py-2 pr-2 font-medium">Aantal</th>
              <th className="py-2 pr-2 font-medium">Eenheid</th>
              <th className="py-2 pr-2 font-medium">Stukprijs</th>
              <th className="py-2 font-medium">Totaal</th>
            </tr>
          </thead>
          <tbody>
            {c.items.map((row, i) => (
              <tr key={i} className="border-b border-border/60">
                <td className="py-2 pr-2 text-text">{humanizePricingLineDescription(row.description)}</td>
                <td className="py-2 pr-2 tabular-nums">{formatQuantityNl(row.quantity)}</td>
                <td className="py-2 pr-2">{formatMeasureUnitNl(row.unit)}</td>
                <td className="py-2 pr-2 tabular-nums">{formatEuroNl(row.unit_price)}</td>
                <td className="py-2 font-medium tabular-nums">{formatEuroNl(row.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (c.type === 'text') {
    return <p className="whitespace-pre-wrap text-sm text-text">{c.text}</p>;
  }
  if (c.type === 'disclaimer') {
    return <p className="text-xs text-text-muted">{c.text}</p>;
  }
  return null;
}

function Section({ s }: { s: QuoteSection }) {
  return (
    <section className="mb-6 border-b border-border pb-6 last:mb-0 last:border-0 last:pb-0">
      <h3 className="mb-3 text-base font-semibold text-text">{s.title}</h3>
      <div className="space-y-4">
        {s.content.map((c, i) => (
          <ContentBlock key={i} c={c} />
        ))}
      </div>
    </section>
  );
}

export function QuoteDocumentView({
  doc,
  onPrint,
}: {
  doc: QuoteDocument;
  /** Bijv. scroll naar dit blok + print — levert schone PDF i.p.v. hele pagina */
  onPrint?: () => void;
}) {
  const sections = doc.sections ?? [];

  const handlePrint = () => {
    if (onPrint) onPrint();
    else globalThis.window?.print();
  };

  return (
    <div
      id="printable-offerte"
      className="print:border-none print:shadow-none rounded-lg border border-border bg-surface-card p-5 print:bg-white"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-lg font-semibold text-text">Offerte</h2>
          {doc.generated_at && (
            <span className="text-xs text-text-muted">
              {new Date(doc.generated_at).toLocaleString('nl-NL')}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handlePrint}
          className="print:hidden flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:bg-surface-card"
        >
          <Printer className="h-4 w-4" />
          {onPrint ? 'PDF maken (offerte)' : 'Afdrukken / PDF'}
        </button>
      </div>
      {(doc.total_excl_vat != null || doc.total_incl_vat != null) && (
        <dl className="mb-6 grid gap-2 sm:grid-cols-2">
          {doc.total_excl_vat != null && (
            <div className="flex justify-between rounded-lg bg-surface px-3 py-2">
              <dt className="text-text-muted">Totaal excl. BTW</dt>
              <dd className="font-semibold text-text">{formatEuroNl(doc.total_excl_vat)}</dd>
            </div>
          )}
          {doc.total_incl_vat != null && (
            <div className="flex justify-between rounded-lg bg-surface px-3 py-2">
              <dt className="text-text-muted">Totaal incl. BTW</dt>
              <dd className="font-semibold text-primary">{formatEuroNl(doc.total_incl_vat)}</dd>
            </div>
          )}
          {doc.vat_rate != null && (
            <div className="flex justify-between text-sm sm:col-span-2">
              <dt className="text-text-muted">BTW-tarief</dt>
              <dd>{new Intl.NumberFormat('nl-NL', { style: 'percent', maximumFractionDigits: 2 }).format(doc.vat_rate)}</dd>
            </div>
          )}
        </dl>
      )}
      <div className="space-y-2">
        {[...sections].sort((a, b) => a.order - b.order).map((s) => (
          <Section key={s.section_id} s={s} />
        ))}
      </div>
      {doc.disclaimer && !sections.some((s) => s.section_id === 'disclaimer') && (
        <p className="mt-6 border-t border-border pt-4 text-xs text-text-muted">{doc.disclaimer}</p>
      )}
    </div>
  );
}
